// xlsx-populate 브라우저 번들 로드
// @ts-ignore
import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate';

// 동적 컬럼 - 엑셀 헤더 그대로 사용
export interface OrderRow {
  [key: string]: string | number;
}

// RichText 등 복잡한 셀 값을 단순 문자열/숫자로 변환
export function flattenValue(val: any): string | number {
  if (val == null) return '';
  if (typeof val === 'string' || typeof val === 'number') return val;
  // RichText 객체: text() 메서드가 있으면 호출
  if (typeof val.text === 'function') return val.text();
  // 그 외 객체는 문자열로
  try { return String(val); } catch { return ''; }
}

// 엑셀 날짜 시리얼 → 문자열 변환
export function excelDateToString(serial: any): string {
  if (!serial) return '';
  if (typeof serial === 'string') return serial;
  if (typeof serial !== 'number') return String(serial);
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400;
  const fractionalDay = serial - Math.floor(serial);
  const totalSeconds = Math.floor(86400 * fractionalDay);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const date = new Date(utcValue * 1000);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  return `${y}-${m}-${d} ${hh}:${mm}`;
}

// 날짜 컬럼 판별
const DATE_COLUMNS = ['발송일', '결제일', '발주확인일', '발송기한', '발송처리일', '송장출력일', '주문일시'];

export interface ParseResult {
  headers: string[];
  rows: OrderRow[];
}

export async function parseExcelFile(file: File, password: string): Promise<ParseResult> {
  const arrayBuffer = await file.arrayBuffer();

  // 여러 비밀번호 시도: 입력값 → 0000 → 비밀번호 없이
  let wb;
  const passwords = [password, '0000', ''];
  for (const pw of passwords) {
    try {
      wb = pw
        ? await XlsxPopulate.fromDataAsync(arrayBuffer, { password: pw })
        : await XlsxPopulate.fromDataAsync(arrayBuffer);
      break;
    } catch {
      continue;
    }
  }
  if (!wb) throw new Error('파일을 열 수 없습니다');
  const ws = wb.sheet(0);

  // 숨김 행/열 해제
  const used = ws.usedRange();
  if (!used) return { headers: [], rows: [] };
  const maxR = used.endCell().rowNumber();
  const maxC = used.endCell().columnNumber();
  for (let r = 1; r <= maxR; r++) {
    try { ws.row(r).hidden(false); } catch {}
  }
  for (let c = 1; c <= maxC; c++) {
    try { ws.column(c).hidden(false); } catch {}
  }

  const maxRow = used.endCell().rowNumber();
  const maxCol = used.endCell().columnNumber();

  // 헤더 행 자동 감지: '주문번호' 컬럼이 있는 행 찾기
  let headerRowNum = 1;
  for (let r = 1; r <= Math.min(5, maxRow); r++) {
    const val = String(ws.cell(r, 1).value() || '').trim();
    if (val === '주문번호') { headerRowNum = r; break; }
  }

  const headers: string[] = [];
  for (let c = 1; c <= maxCol; c++) {
    const val = String(ws.cell(headerRowNum, c).value() || '').trim();
    headers.push(val);
  }

  const rows: OrderRow[] = [];
  for (let r = headerRowNum + 1; r <= maxRow; r++) {
    const row: OrderRow = {};
    let hasProductOrderNo = false;

    for (let c = 0; c < headers.length; c++) {
      const header = headers[c];
      if (!header) continue;
      let val = flattenValue(ws.cell(r, c + 1).value());

      // 날짜 컬럼 변환
      if (DATE_COLUMNS.includes(header) && typeof val === 'number') {
        val = excelDateToString(val);
      }

      row[header] = val;

      if (header === '상품주문번호' && val) hasProductOrderNo = true;
    }

    if (!hasProductOrderNo) continue;
    rows.push(row);
  }

  return { headers, rows };
}
